/**
 * Project List Page
 * @implements US-014 - Project Listing & Navigation
 * @implements US-016 - Project Archival
 * @implements US-017 - Project Deletion
 * @implements FS-002.3.1 - View All Projects
 * @implements FS-002.3.2 - Project Navigation
 * @implements FS-002.5 - Project Lifecycle States
 * @implements TS-066 - Archived Projects Section
 * @implements TS-070 - Delete Project UI (Pending Deletion section)
 *
 * Displays a list of all user's projects with state badges.
 * Allows navigation to individual projects and project creation.
 * Groups projects by lifecycle state:
 * - Active projects shown first
 * - Pending deletion projects in a separate grayed out section
 * - Archived projects in a collapsible section
 *
 * Route: /projects
 */

import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  DeleteForever as DeleteForeverIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import {
  LifecycleStateBadge,
  ControlStateBadge,
  LifecycleState,
  ControlState,
} from '@/components/project';

/**
 * GraphQL query for fetching user's projects
 * @implements FS-002.3.1 - View All Projects
 * @implements TS-070 - Delete Project UI (includes deletion fields)
 *
 * Note: This is defined inline until codegen is run.
 * After running `yarn codegen`, use the generated useMyProjectsQuery hook.
 */
const MY_PROJECTS_QUERY = gql`
  query MyProjects {
    myProjects {
      id
      title
      description
      lifecycleState
      controlState
      createdAt
      scheduledDestructionDate
    }
  }
`;

/**
 * Project type from the GraphQL response
 */
interface Project {
  id: string;
  title: string;
  description: string | null;
  lifecycleState: LifecycleState;
  controlState: ControlState;
  createdAt: string;
  scheduledDestructionDate: string | null;
}

/**
 * Response type for MyProjects query
 */
interface MyProjectsResponse {
  myProjects: Project[];
}

/**
 * ProjectListPage - Displays list of all user's projects
 *
 * @implements US-014 - Project Listing & Navigation
 * @implements US-016 - Project Archival
 * @implements FS-002.3.1 - View All Projects
 * @implements FS-002.3.2 - Project Navigation
 * @implements TS-066 - Archived Projects Section
 *
 * Features:
 * - Displays all projects with title and state badges
 * - Groups projects by lifecycle state (ACTIVE vs ARCHIVED)
 * - Active projects shown first, archived in collapsible section
 * - Archived projects have grayed out appearance with archive icon
 * - Click on project navigates to /projects/:id (read-only for archived)
 * - "Create New Project" button links to /projects/new
 * - Loading and empty state handling
 */
export const ProjectListPage = () => {
  const navigate = useNavigate();
  const [archivedExpanded, setArchivedExpanded] = useState(false);

  // Fetch user's projects
  const { data, loading, error } = useQuery<MyProjectsResponse>(
    MY_PROJECTS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const projects = data?.myProjects ?? [];

  // Separate projects by lifecycle state for display
  // Note: Backend returns lowercase lifecycle states, so we normalize for comparison
  // @implements TS-070 - Delete Project UI (Pending Deletion section)
  const activeProjects = projects.filter(
    (p) => p.lifecycleState?.toUpperCase() === 'ACTIVE',
  );
  const pendingDeletionProjects = projects.filter(
    (p) => p.lifecycleState?.toUpperCase() === 'PENDING_DELETION',
  );
  const archivedProjects = projects.filter(
    (p) => p.lifecycleState?.toUpperCase() === 'ARCHIVED',
  );

  /**
   * Handle project click - navigate to project view
   * @implements FS-002.3.2 - Project Navigation
   */
  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  /**
   * Handle create new project click
   * @implements FS-002.1.1 - Create New Project
   */
  const handleCreateProject = () => {
    navigate('/projects/new');
  };

  // Render loading state
  if (loading && !data) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">
            Failed to load projects: {error.message}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4" component="h1">
            My Projects
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateProject}
          >
            Create New Project
          </Button>
        </Box>

        {/* Empty state */}
        {projects.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" gutterBottom>
              No projects yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first project to get started with the AI PM
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateProject}
            >
              Create Your First Project
            </Button>
          </Paper>
        )}

        {/* Active Projects */}
        {activeProjects.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <List disablePadding>
              {activeProjects.map((project, index) => (
                <ListItemButton
                  key={project.id}
                  onClick={() => handleProjectClick(project.id)}
                  divider={index < activeProjects.length - 1}
                  sx={{
                    py: 2,
                    px: 3,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight={500}>
                        {project.title}
                      </Typography>
                    }
                    secondary={
                      project.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {project.description}
                        </Typography>
                      )
                    }
                  />
                  <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                    <LifecycleStateBadge state={project.lifecycleState} />
                    {project.controlState?.toUpperCase() !== 'NORMAL' && (
                      <ControlStateBadge state={project.controlState} />
                    )}
                  </Stack>
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}

        {/* Pending Deletion Projects Section */}
        {/* @implements TS-070 - Delete Project UI (Pending Deletion section) */}
        {pendingDeletionProjects.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              border: '1px solid',
              borderColor: 'error.light',
              backgroundColor: 'error.lighter',
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'error.light',
                backgroundColor: 'error.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <DeleteForeverIcon fontSize="small" sx={{ color: 'white' }} />
              <Typography variant="subtitle2" sx={{ color: 'white' }}>
                Pending Deletion ({pendingDeletionProjects.length})
              </Typography>
            </Box>
            <List disablePadding>
              {pendingDeletionProjects.map((project, index) => {
                // Format the scheduled destruction date
                const destructionDate = project.scheduledDestructionDate
                  ? new Date(
                      project.scheduledDestructionDate,
                    ).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : 'Unknown';

                return (
                  <ListItemButton
                    key={project.id}
                    onClick={() => handleProjectClick(project.id)}
                    divider={index < pendingDeletionProjects.length - 1}
                    sx={{
                      py: 2,
                      px: 3,
                      opacity: 0.6,
                      backgroundColor: 'grey.50',
                      '&:hover': { opacity: 0.8, backgroundColor: 'grey.100' },
                    }}
                    disabled
                  >
                    <DeleteForeverIcon
                      fontSize="small"
                      sx={{ mr: 2, color: 'error.main' }}
                    />
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle1"
                          fontWeight={500}
                          color="text.disabled"
                          sx={{ textDecoration: 'line-through' }}
                        >
                          {project.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="error.main">
                          Scheduled for permanent deletion on {destructionDate}
                        </Typography>
                      }
                    />
                    <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                      <LifecycleStateBadge state={project.lifecycleState} />
                    </Stack>
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        )}

        {/* Archived Projects Section - Collapsible Accordion */}
        {/* @implements TS-066 - Archived Projects Section */}
        {archivedProjects.length > 0 && (
          <Accordion
            expanded={archivedExpanded}
            onChange={(_, expanded) => setArchivedExpanded(expanded)}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              '&:before': { display: 'none' },
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="archived-projects-content"
              id="archived-projects-header"
              sx={{
                backgroundColor: 'grey.50',
                '&:hover': { backgroundColor: 'grey.100' },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <ArchiveIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  Archived Projects ({archivedProjects.length})
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List disablePadding>
                {archivedProjects.map((project, index) => (
                  <ListItemButton
                    key={project.id}
                    onClick={() => handleProjectClick(project.id)}
                    divider={index < archivedProjects.length - 1}
                    sx={{
                      py: 2,
                      px: 3,
                      opacity: 0.75,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    {/* Archive icon indicator */}
                    <ArchiveIcon
                      fontSize="small"
                      sx={{ mr: 2, color: 'text.disabled' }}
                    />
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle1"
                          fontWeight={500}
                          color="text.secondary"
                        >
                          {project.title}
                        </Typography>
                      }
                      secondary={
                        project.description && (
                          <Typography
                            variant="body2"
                            color="text.disabled"
                            sx={{
                              mt: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {project.description}
                          </Typography>
                        )
                      }
                    />
                    <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                      <LifecycleStateBadge state={project.lifecycleState} />
                    </Stack>
                  </ListItemButton>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Container>
  );
};
